import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { propertyCommand } from '../../command/property.command';
import { Property } from '../../model/property';
import { PropertyCommandHandlerResolver } from '../../service/command/property-command-handler-resolver.service';
import { PropertyExportCommandHandler } from '../../service/command/property-export-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels } from '../../../accounting/model/dimension-labels';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-property-manager',
    templateUrl: './property-manager.component.html',
    styleUrls: ['./../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss'],
    providers: [PropertyCommandHandlerResolver, PropertyExportCommandHandler],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PropertyManagerComponent extends MultiselectObjectManagerComponentBase<Property> {
  menuDefinition: MenuItemDefinition[];
  constructor(
    private readonly columnDefinitionService: ColumnDefinitionService,
    private readonly _dataService: PropertyService,
    private readonly _companyService: CompanyService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _customPropertyHelper: CustomPropertyHelper,
    dispatcher: CommandDispatcher,
    handlerResolver: PropertyCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccountService: UserAccountService
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);

    this.title = 'PROPERTY.LIST_HEADER';
    this.columnDefinition = this.columnDefinitionService.create('Property');

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', propertyCommand.export)];
    this.preferencesKey = 'rent.property-manager.list';

    this.AddSubscription(
      _userAccountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditProperty)) {
            this.addContextMenuItemDefinition('PROPERTY.MOVE_TO_COMPANY', propertyCommand.moveToCompany);
            this.addContextMenuItemDefinition('PROPERTY_TAX.MENU_ITEM_TITLE', propertyCommand.propertyTaxRental);
          }
        },
      })
    );
  }

  ngOnInit() {
    this.AddSubscription(this._dimensionLabelsService.getDefaultLabels().subscribe(labels => this.applyDimensionLabels(labels)));

    this.AddSubscription(
      forkJoin([this._dataService.getFilterDefinition(), this._customPropertyService.getList()]).subscribe(([definition, customProperties]) => {
        this.defaultFilters = definition.defaultFields;

        const baseFilters = [
          new FilterDefinitionItem(
            'COMMON.COMPANY',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'PROPERTY.NAME',
            'propertyName',
            definition.propertyName,
            null,
            definition.selectedFields.includes('propertyName')
          ),
          new FilterDefinitionItem(
            'PROPERTY.MUNICIPALITY',
            'municipality',
            definition.municipality,
            null,
            definition.selectedFields.includes('municipality')
          ),
        ];

        const customPropertyFilters = this._customPropertyHelper.generateCustomPropertyFilters(
          customProperties,
          definition,
          CustomPropertyVisibilityType.Property
        );
        this.filterDefinition = [...baseFilters, ...customPropertyFilters];
        this._customPropertyHelper.createCustomPropertyColumnDefinition(
          customProperties,
          this.columnDefinition,
          CustomPropertyVisibilityType.Property
        );
      })
    );

    this.addEditDeleteCommands();
  }

  override filterInternal(filterState: any) {
    this._customPropertyHelper.applyCustomPropertyFilters(filterState);
    this._dimensionLabelsService.getForCompany(filterState?.company).subscribe(labels => this.applyDimensionLabels(labels));
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'costPlace', labels.costPlaceLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'costBearer', labels.costBearerLabel);
    this.columnDefinition = [...this.columnDefinition];
  }
}
